import React, { useEffect, useState, useRef, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

export default function RichEditor({ value, onChange, placeholder }) {
  const editorRef    = useRef(null);
  const fileInputRef = useRef(null);
  const [mode, setMode]     = useState('visual');
  const [htmlVal, setHtmlVal] = useState(value || '');

  // Sync content into editor on first load / mode switch
  useEffect(() => {
    if (editorRef.current && mode === 'visual') {
      editorRef.current.innerHTML = value || '';
    }
  }, [mode]); // eslint-disable-line

  // When value changes from outside (e.g. loading edit data), sync to editor
  useEffect(() => {
    if (editorRef.current && mode === 'visual' && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
    setHtmlVal(value || '');
  }, [value]); // eslint-disable-line

  const exec = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
    syncContent();
  }, []); // eslint-disable-line

  const syncContent = () => {
    const html = editorRef.current?.innerHTML || '';
    setHtmlVal(html);
    onChange(html);
  };

  const insertHTML = (html) => {
    editorRef.current?.focus();
    document.execCommand('insertHTML', false, html);
    syncContent();
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('cover_image', file);
    try {
      const res = await api.post('/admin/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).catch(async () => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve({ data: { url: ev.target.result } });
          reader.readAsDataURL(file);
        });
      });
      const url = res.data?.url || res.data;
      const fullUrl = url.startsWith('data:') ? url : `${IMG_BASE}${url}`;
      insertHTML(`<img src="${fullUrl}" alt="foto" style="max-width:100%;height:auto;border-radius:6px;margin:8px 0"/>`);
    } catch {
      toast.error('Gabim gjatë ngarkimit të fotos.');
    }
    e.target.value = '';
  };

  const insertLink = () => {
    const url = window.prompt('Shkruani URL-in:', 'https://');
    if (!url) return;
    const text = window.getSelection()?.toString() || url;
    exec('insertHTML', `<a href="${url}" target="_blank" rel="noopener">${text}</a>`);
  };

  const insertVideo = () => {
    const url = window.prompt('YouTube/Vimeo URL:', 'https://www.youtube.com/watch?v=');
    if (!url) return;
    let embed = url;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) embed = `https://player.vimeo.com/video/${vm[1]}`;
    insertHTML(`<div style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:16px 0"><iframe src="${embed}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0" allowfullscreen loading="lazy"></iframe></div>`);
  };

  const insertTable = () => {
    const rows = parseInt(window.prompt('Numri i rreshtave:', '3')) || 3;
    const cols = parseInt(window.prompt('Numri i kolonave:', '3')) || 3;
    let html = '<table style="width:100%;border-collapse:collapse;margin:16px 0"><tbody>';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let col = 0; col < cols; col++) {
        const tag   = r === 0 ? 'th' : 'td';
        const style = r === 0
          ? 'padding:10px 12px;border:1px solid #ddd;background:#f5f5f5;font-weight:600;text-align:left'
          : 'padding:10px 12px;border:1px solid #ddd';
        html += `<${tag} style="${style}">${r === 0 ? `Kolona ${col+1}` : '...'}</${tag}>`;
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    insertHTML(html);
  };

  const switchToHTML = () => {
    const html = editorRef.current?.innerHTML || '';
    setHtmlVal(html);
    setMode('html');
  };

  const switchToVisual = () => {
    onChange(htmlVal);
    setMode('visual');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = htmlVal;
    }, 0);
  };

  const tools = [
    { group: 'format', items: [
      { label: 'B',  title: 'Bold',      action: () => exec('bold'),          style: {fontWeight:'bold'} },
      { label: 'I',  title: 'Italic',    action: () => exec('italic'),        style: {fontStyle:'italic'} },
      { label: 'U',  title: 'Nënvizim', action: () => exec('underline'),     style: {textDecoration:'underline'} },
      { label: 'S',  title: 'Fshirje',  action: () => exec('strikeThrough'), style: {textDecoration:'line-through'} },
    ]},
    { group: 'heading', items: [
      { label: 'H1', title: 'Titull 1', action: () => exec('formatBlock','<h1>') },
      { label: 'H2', title: 'Titull 2', action: () => exec('formatBlock','<h2>') },
      { label: 'H3', title: 'Titull 3', action: () => exec('formatBlock','<h3>') },
      { label: '¶',  title: 'Paragraf', action: () => exec('formatBlock','<p>') },
    ]},
    { group: 'list', items: [
      { label: '≡',  title: 'Lista',             action: () => exec('insertUnorderedList') },
      { label: '1.', title: 'Lista e numëruar',  action: () => exec('insertOrderedList') },
      { label: '❝',  title: 'Citat',             action: () => exec('formatBlock','<blockquote>') },
    ]},
    { group: 'align', items: [
      { label: '⬅', title: 'Majtas',   action: () => exec('justifyLeft') },
      { label: '↔', title: 'Qendër',  action: () => exec('justifyCenter') },
      { label: '➡', title: 'Djathtas', action: () => exec('justifyRight') },
    ]},
    { group: 'insert', items: [
      { label: '🔗', title: 'Shto link',    action: insertLink },
      { label: '🖼', title: 'Shto foto',    action: () => fileInputRef.current.click() },
      { label: '▶',  title: 'Shto video',   action: insertVideo },
      { label: '⊞',  title: 'Shto tabelë', action: insertTable },
      { label: '—',  title: 'Vijë ndarëse', action: () => insertHTML('<hr style="border:none;border-top:2px solid #e5e7eb;margin:20px 0"/>') },
    ]},
    { group: 'color', items: [
      { label: 'A', title: 'Ngjyra e tekstit', style:{color:'var(--blue-700)',fontWeight:700},
        action: () => { const col = window.prompt('Ngjyra (hex):', '#0B4F8A'); if(col) exec('foreColor', col); } },
    ]},
  ];

  return (
    <div className="rich-editor-wrap">
      <div className="re-toolbar">
        {tools.map((group, gi) => (
          <React.Fragment key={gi}>
            <div className="re-tool-group">
              {group.items.map((t, ti) => (
                <button key={ti} type="button" className="re-tool-btn"
                  title={t.title} style={t.style || {}} onClick={t.action}>
                  {t.label}
                </button>
              ))}
            </div>
            {gi < tools.length - 1 && <div className="re-sep"/>}
          </React.Fragment>
        ))}
        <div style={{flex:1}}/>
        <div className="re-tool-group">
          <button type="button"
            className={`re-tool-btn mode-btn ${mode === 'visual' ? 'active' : ''}`}
            onClick={switchToVisual}>Vizual</button>
          <button type="button"
            className={`re-tool-btn mode-btn ${mode === 'html' ? 'active' : ''}`}
            onClick={switchToHTML}>HTML</button>
        </div>
      </div>

      {mode === 'visual' ? (
        <div ref={editorRef} className="re-content" contentEditable
          suppressContentEditableWarning
          onInput={syncContent} onBlur={syncContent}
          data-placeholder={placeholder || 'Shkruani përmbajtjen këtu...'}
        />
      ) : (
        <textarea className="re-html-area" value={htmlVal}
          onChange={e => { setHtmlVal(e.target.value); onChange(e.target.value); }}
          placeholder="Kodi HTML..."/>
      )}

      <input ref={fileInputRef} type="file" accept="image/*"
        style={{display:'none'}} onChange={handleImageUpload}/>

      <div className="re-statusbar">
        <span>💡 Zgjidhni tekstin → B/I/U formatim | 🖼 foto | ▶ video | ⊞ tabelë | 🔗 link | HTML për kod direkt</span>
      </div>
    </div>
  );
}
